import request from '../../utils/request';

/**
 * Token counts. input_tokens is the TOTAL input processed, including cache reads and
 * cache writes; the cache fields are the cached portions of it.
 */
export interface ITokenCounts {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens?: number;
  cache_write_tokens?: number;
}

export interface ITokenUsageLog extends ITokenCounts {
  id: number;
  service: string;
  model: string;
  tenant_id: number | null;
  executed_at: string;
}

export interface ITokenUsageResult {
  data: ITokenUsageLog[];
  count: number;
  page: number;
  limit: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCacheReadTokens?: number;
  totalCacheWriteTokens?: number;
}

export interface ITokenSummary {
  totals: ITokenCounts & { call_count: number };
  by_service: (ITokenCounts & { service: string; call_count: number })[];
  by_model: (ITokenCounts & { model: string; call_count: number })[];
  daily: (ITokenCounts & { date: string })[];
}

export interface IModelPricing {
  model: string;
  input_cost_per_1m: number;
  output_cost_per_1m: number;
  /** null = not configured; cached tokens are then priced at the input rate. */
  cache_read_cost_per_1m?: number | null;
  cache_write_cost_per_1m?: number | null;
  is_active: boolean;
  updated_at: string | null;
}

class AiTokenUsageService {
  list = async (page = 1, limit = 50, service?: string, model?: string, sort?: 'date' | 'tokens'): Promise<ITokenUsageResult> => {
    const params: Record<string, any> = { page, limit };
    if (service) params.service = service;
    if (model) params.model = model;
    if (sort) params.sort = sort;
    const res = await request({ url: '/ai-token-usage', method: 'GET', params });
    return res.data.body.data;
  };

  summary = async (days = 30): Promise<ITokenSummary> => {
    const res = await request({ url: '/ai-token-usage/summary', method: 'GET', params: { days } });
    return res.data.body.data;
  };

  getPricing = async (): Promise<IModelPricing[]> => {
    const res = await request({ url: '/ai-token-usage/pricing', method: 'GET' });
    return res.data.body.data;
  };

  upsertPricing = async (
    model: string,
    inputCostPer1M: number,
    outputCostPer1M: number,
    isActive: boolean,
    cacheReadCostPer1M: number | null,
    cacheWriteCostPer1M: number | null,
  ): Promise<IModelPricing> => {
    const res = await request({
      url: '/ai-token-usage/pricing',
      method: 'POST',
      data: {
        model,
        input_cost_per_1m: inputCostPer1M,
        output_cost_per_1m: outputCostPer1M,
        cache_read_cost_per_1m: cacheReadCostPer1M,
        cache_write_cost_per_1m: cacheWriteCostPer1M,
        is_active: isActive,
      },
    });
    return res.data.body.data;
  };
}

export default new AiTokenUsageService();
